import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, Button, Paper, IconButton, Tooltip, MenuItem, Autocomplete,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BRAND } from "../../styles/theme";
import type { CollectorCtx } from "../../pages/CollectorDetail";
import { setConfigValue, clearConfigValue, removeConfigKey, CONFIG_KEY_TYPES, ConfigKeyType, ConfigKeyMeta } from "../../services/collectors";
import { SystemChip } from "./chips";
import ConfirmDialog from "./ConfirmDialog";
import {
  splitAccount, joinAccount, accountFieldError, accountCountry, ACCOUNT_LABELS, type AccountField,
} from "../../lib/transferAccount";

const READ_ONLY = new Set(["apiKeyHash", "apiKeyLastUse"]);
const LONG      = new Set(["contract_html"]);

const GROUPS: [string, (k: string) => boolean][] = [
  ["System",               (k) => READ_ONLY.has(k)],
  ["Contract",             (k) => k.startsWith("contract")],
  ["Address",              (k) => k.startsWith("data_address")],
  ["Legal representative", (k) => k.startsWith("data_power")],
  ["Vendor web style",     (k) => k.startsWith("web-style")],
];
const GROUP_ORDER = ["General", "Contract", "Address", "Legal representative", "Vendor web style", "System"];
const groupOf = (k: string) => GROUPS.find(([, f]) => f(k))?.[0] ?? "General";

const squareInput = { "& .MuiOutlinedInput-root": { borderRadius: 1 } };

/** Input tipado según la clave (enum/bool/rate/number/long/combo del catálogo/text). */
function ValueInput({ k, value, onChange, disabled, meta, country }: { k: string; value: string; onChange: (v: string) => void; disabled?: boolean; meta?: ConfigKeyMeta; country: string }) {
  const t: ConfigKeyType | undefined = CONFIG_KEY_TYPES[k];
  // CP-481: widget "account" del catálogo (transfer_to_account) → 4 campos, como webadmin1.
  // También por nombre de clave: en sandbox el catálogo CONFIGKEYS no tiene esta key.
  if (meta?.widget === "account" || k === "transfer_to_account") return <AccountInput key={k} value={value} onChange={onChange} disabled={disabled} country={country} />;
  if (t?.kind === "enum") {
    return <TextField select fullWidth label="Value" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} sx={squareInput}>
      {t.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>;
  }
  if (t?.kind === "bool") {
    return <TextField select fullWidth label="Value" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} sx={squareInput}>
      <MenuItem value="true">true</MenuItem><MenuItem value="false">false</MenuItem>
    </TextField>;
  }
  if (t?.kind === "rate") {
    const n = Number(value);
    return <TextField fullWidth label="Value" type="number" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      inputProps={{ step: "0.00001", min: 0, max: 1 }} error={value !== "" && (isNaN(n) || n < 0 || n > 1)}
      helperText="Fraction between 0 and 1 (0.05 = 5%)." sx={squareInput} />;
  }
  if (t?.kind === "number") {
    return <TextField fullWidth label="Value" type="number" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} sx={squareInput} />;
  }
  if (LONG.has(k) || t?.kind === "long") {
    return <TextField fullWidth multiline minRows={8} label="Value" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      sx={{ ...squareInput, "& textarea": { fontFamily: "monospace", fontSize: 12 } }} />;
  }
  // CP-375: sin override manual en CONFIG_KEY_TYPES, pero el catálogo real (CONFIGKEYS) ya trae
  // las opciones — mismo mecanismo dinámico que usaba webadmin1 (widget "combo" + value_str).
  if (meta?.widget === "combo" && meta.options?.length) {
    return <TextField select fullWidth label="Value" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} sx={squareInput}>
      {meta.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>;
  }
  return <TextField fullWidth label="Value" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} sx={squareInput} />;
}

const ACCOUNT_FIELDS: AccountField[] = ["bank", "account", "taxId", "name"];

/**
 * CP-481: `banco:cuenta:id_fiscal:nombre` editado en 4 campos. Cada cambio vuelve a
 * armar el string y lo pasa a `onChange`, así Save sigue usando la regex del catálogo.
 * Los campos se guardan en estado local para no perder lo que se tipea mientras el
 * valor unido todavía no es válido (p. ej. un campo intermedio vacío).
 */
function AccountInput({ value, onChange, disabled, country }: { value: string; onChange: (v: string) => void; disabled?: boolean; country: string }) {
  const c = accountCountry(country);
  const [parts, setParts] = useState(() => splitAccount(value));
  const set = (f: AccountField, v: string) => {
    const next = { ...parts, [f]: v };
    setParts(next); onChange(joinAccount(next));
  };
  return (
    <Stack spacing={1.5}>
      {ACCOUNT_FIELDS.map((f) => {
        const e = accountFieldError(c, f, parts[f]);
        return <TextField key={f} fullWidth label={ACCOUNT_LABELS[c][f]} value={parts[f]} disabled={disabled}
          onChange={(ev) => set(f, ev.target.value)} error={!!e} helperText={e ?? " "}
          inputProps={f === "name" ? undefined : { inputMode: f === "taxId" && c === "MX" ? "text" : "numeric", style: { fontFamily: "monospace" } }}
          sx={squareInput} />;
      })}
    </Stack>
  );
}

export default function ConfigTab({ ctx }: { ctx: CollectorCtx }) {
  const { collector, config, catalog, reload, notify } = ctx;
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") ?? "";
  const setQ = (v: string) => { const n = new URLSearchParams(sp); if (v) n.set("q", v); else n.delete("q"); setSp(n, { replace: true }); };

  const [edit, setEdit]       = useState<{ key: string; value: string; isNew: boolean } | null>(null);
  const [view, setView]       = useState<string | null>(null);
  const [clearK, setClearK]   = useState<string | null>(null);
  const [removeK, setRemoveK] = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);

  const keys = useMemo(() => {
    const s = q.trim().toLowerCase();
    return Object.keys(config).filter((k) =>
      !s || k.toLowerCase().includes(s) || String(config[k] ?? "").toLowerCase().includes(s) || (catalog[k]?.description ?? "").toLowerCase().includes(s));
  }, [config, catalog, q]);
  const grouped = useMemo(() => {
    const g: Record<string, string[]> = {};
    keys.forEach((k) => (g[groupOf(k)] ??= []).push(k));
    Object.values(g).forEach((arr) => arr.sort());
    return g;
  }, [keys]);
  // Solo claves del catálogo que apliquen al país del procesador (el backend
  // rechaza las demás) y que el collector todavía no tenga.
  const country = collector.paymentsProcessor.slice(0, 2);
  const available = useMemo(() =>
    Object.keys(catalog).filter((k) =>
      !(k in config) && !READ_ONLY.has(k) && !catalog[k].readOnly &&
      (!catalog[k].country || catalog[k].country.includes(country))).sort(), [catalog, config, country]);

  // CP-407: el toast de éxito recién cuando reload() terminó; si la escritura o el
  // reload fallan se muestra el error en el diálogo (que queda abierto) y no hay toast.
  async function saveEdit() {
    if (!edit) return;
    setBusy(true); setErr(null);
    try {
      await setConfigValue(collector, edit.key, edit.value);
      await reload();
      notify(`${edit.key} ${edit.isNew ? "added" : "saved"}`);
      setEdit(null);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  async function doClear() {
    if (!clearK) return;
    setBusy(true); setErr(null);
    try {
      await clearConfigValue(collector, clearK);
      await reload();
      notify(`${clearK} cleared`);
      setClearK(null);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  async function doRemove() {
    if (!removeK) return;
    setBusy(true); setErr(null);
    try {
      await removeConfigKey(collector, removeK);
      await reload();
      notify(`${removeK} removed`);
      setRemoveK(null);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const editValueOk = (() => {
    if (!edit) return false;
    const t = CONFIG_KEY_TYPES[edit.key];
    if (t?.kind === "rate") { const n = Number(edit.value); return edit.value !== "" && !isNaN(n) && n >= 0 && n <= 1; }
    // Misma validación que aplica el backend al upsert (regex del catálogo).
    const re = catalog[edit.key]?.validation;
    if (re) { try { return new RegExp(re).test(edit.value); } catch { return true; } }
    return true;
  })();
  // CP-488: sin cambios no se manda nada (adminapi rechaza incluso reescribir el mismo
  // payments_processor).
  const editUnchanged = !!edit && !edit.isNew && edit.value === String(config[edit.key] ?? "");
  const editValueHint = edit && catalog[edit.key]?.validation && !editValueOk
    ? `Must match ${catalog[edit.key].validation}` : null;

  // CP-528: si no se elige payments_sub_processor, el servicio arranca sobre la
  // cuenta propia vieja (bindpsp) sin ningún aviso — solo aplica a AR#BIND, que es
  // donde existe esa cuenta vieja (ver el mensaje de error de setAccountEnabled en
  // services/collectors.ts, que menciona bindpsp/cvucollect_cucuru).
  const missingSubProcessor = collector.paymentsProcessor === "AR#BIND" && !("payments_sub_processor" in config);

  return (
    <>
      {missingSubProcessor && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setEdit({ key: "payments_sub_processor", value: "", isNew: true })}>Set now</Button>}>
          <code>payments_sub_processor</code> is not set for this collector. Without it, the service silently defaults to the old own BIND account (bindpsp) instead of the one that actually applies (e.g. cvucollect_cucuru).
        </Alert>
      )}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Key, value or description…" value={q}
          onChange={(e) => setQ(e.target.value)} sx={{ width: 320 }} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{keys.length} keys set · {available.length} available in catalog</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEdit({ key: "", value: "", isNew: true })} sx={{ height: 40 }}>
          Add config
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: "38%" }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {q ? `No keys match “${q}”.` : "No configuration yet."}{" "}
                <Button size="small" onClick={() => setEdit({ key: "", value: "", isNew: true })}>Add config</Button>
              </TableCell></TableRow>
            ) : GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
              <ConfigGroup key={g} title={g} keys={grouped[g]} ctx={ctx} busy={busy}
                onEdit={(k) => { setErr(null); setEdit({ key: k, value: String(config[k] ?? ""), isNew: false }); }}
                onView={setView} onClear={(k) => { setErr(null); setClearK(k); }} onRemove={(k) => { setErr(null); setRemoveK(k); }} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        <span><b>Clear value</b> keeps the key with an empty value (some integrations read presence, e.g. <code>new_accounts</code>). <b>Remove</b> deletes the key from the collector entirely.</span>
      </Typography>

      {/* Add / Edit */}
      <Dialog open={!!edit} onClose={() => !busy && setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {edit?.isNew ? "Add config" : "Edit config value"}
          <Typography variant="body2" color="text.secondary">
            {collector.id}{edit && !edit.isNew ? <> · <code>{edit.key}</code></> : " · choose a key from the catalog and set its value in one step"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {edit?.isNew && (
              <Autocomplete options={available} value={edit.key || null} disabled={busy} autoHighlight
                onChange={(_, v) => setEdit({ key: v ?? "", value: "", isNew: true })}
                renderOption={(props, k) => (
                  <li {...props} key={k}>
                    <Box><Typography variant="body2" sx={{ fontFamily: "monospace" }}>{k}</Typography>
                      <Typography variant="caption" color="text.secondary">{catalog[k]?.description ?? ""}</Typography></Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Config key" placeholder="Type to search the catalog…" sx={squareInput}
                  helperText={edit.key ? (catalog[edit.key]?.description || "No description in the catalog.") : `${available.length} keys available (keys already set are hidden).`} />} />
            )}
            {edit && !edit.isNew && catalog[edit.key]?.description && (
              <Typography variant="body2" color="text.secondary">{catalog[edit.key].description}</Typography>
            )}
            {edit && (edit.key || !edit.isNew) && (
              <ValueInput k={edit.key} value={edit.value} disabled={busy} meta={catalog[edit.key]} country={country} onChange={(v) => setEdit({ ...edit, value: v })} />
            )}
            {editValueHint && <Typography variant="caption" color="error">{editValueHint}</Typography>}
            {err && <Alert severity="error">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy || !edit?.key || !editValueOk || editUnchanged}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {busy ? "Saving…" : edit?.isNew ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Viewer de valores largos */}
      <Dialog open={!!view} onClose={() => setView(null)} maxWidth="md" fullWidth>
        <DialogTitle>{view}<Typography variant="body2" color="text.secondary">{collector.id} · read-only preview</Typography></DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={16} value={String(config[view ?? ""] ?? "")} InputProps={{ readOnly: true }}
            sx={{ ...squareInput, "& textarea": { fontFamily: "monospace", fontSize: 12 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setView(null)}>Close</Button>
          <Button variant="contained" onClick={() => { const k = view!; setView(null); setEdit({ key: k, value: String(config[k] ?? ""), isNew: false }); }}>Edit</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!clearK} onClose={() => { if (!busy) { setClearK(null); setErr(null); } }} onConfirm={doClear} title="Clear value" okLabel="Clear value" busyLabel="Clearing…">
        The key <code>{clearK}</code> stays on <b>{collector.id}</b> with an empty value. Integrations that check for the key's presence keep working.
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </ConfirmDialog>
      <ConfirmDialog open={!!removeK} onClose={() => { if (!busy) { setRemoveK(null); setErr(null); } }} onConfirm={doRemove} title="Remove config key" okLabel="Remove key" busyLabel="Removing…" danger>
        <code>{removeK}</code> and its value will be removed from <b>{collector.id}</b>. You can add it again later from the catalog.
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </ConfirmDialog>
    </>
  );
}

function ConfigGroup({ title, keys, ctx, busy, onEdit, onView, onClear, onRemove }: {
  title: string; keys: string[]; ctx: CollectorCtx; busy: boolean;
  onEdit: (k: string) => void; onView: (k: string) => void; onClear: (k: string) => void; onRemove: (k: string) => void;
}) {
  const { config, catalog } = ctx;
  return (
    <>
      <TableRow>
        <TableCell colSpan={3} sx={{ pt: 2, pb: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", borderBottom: 0 }}>
          {title}
        </TableCell>
      </TableRow>
      {keys.map((k) => {
        const v = String(config[k] ?? "");
        const label = catalog[k]?.options?.find((o) => o.value === v)?.label;
        const ro = READ_ONLY.has(k) || !!catalog[k]?.readOnly;
        const long = LONG.has(k) || v.length > 120;
        return (
          <TableRow key={k} hover>
            <TableCell>
              <code style={{ background: BRAND.primaryLight, padding: "1px 6px", borderRadius: 6, fontSize: 12.5 }}>{k}</code>
              {catalog[k]?.description && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontStyle: "italic", mt: 0.25 }}>{catalog[k].description}</Typography>
              )}
            </TableCell>
            <TableCell>
              {long ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">{v.length} chars</Typography>
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => onView(k)}>View</Button>
                </Stack>
              ) : v === "" ? (
                <Typography variant="caption" color="text.secondary">(empty)</Typography>
              ) : label ? (
                <Tooltip title={v}><span style={{ fontSize: 13, maxWidth: 360, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>{label}</span></Tooltip>
              ) : (
                <Tooltip title={v}><span style={{ fontFamily: "monospace", fontSize: 13, maxWidth: 360, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>{v}</span></Tooltip>
              )}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              {ro ? <SystemChip /> : (
                <>
                  <Tooltip title="Edit value"><span><IconButton size="small" disabled={busy} onClick={() => onEdit(k)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Clear value (keep key)"><span><IconButton size="small" disabled={busy || v === ""} onClick={() => onClear(k)}><RemoveCircleOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Remove key"><span><IconButton size="small" color="error" disabled={busy} onClick={() => onRemove(k)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
                </>
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

